// src/pages/ErrorReviewPage.jsx
import React, { useEffect, useMemo, useCallback, useRef, useState } from "react";
import axios from "axios"; // оставить для blob запросов
import api from "../api/axios";

import { getEcho, disconnectEcho } from "../lib/echo";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  PdfLoader,
  PdfHighlighter,
  Highlight,
  AreaHighlight,
  Popup,
} from "react-pdf-highlighter";
import "react-pdf-highlighter/dist/style.css";

/* ===================== constants ===================== */
const API_BASE = "https://expertise.gov.kz";
const STAFF_TAB_KEY = "in-expertise";
const STAFF_ROUTE = "/staff-groups";
const STAFF_URL = `${STAFF_ROUTE}?tab=${STAFF_TAB_KEY}#${STAFF_TAB_KEY}`;

/* ===================== highlight colors ===================== */
const HIGHLIGHT_COLORS = [
  { id: "yellow", label: "Жёлтый",  bg: "rgba(253,224,71,0.35)",  border: "#ca8a04", pin: "#ca8a04" },
  { id: "green",  label: "Зелёный", bg: "rgba(74,222,128,0.30)",  border: "#16a34a", pin: "#16a34a" },
  { id: "blue",   label: "Синий",   bg: "rgba(96,165,250,0.30)",  border: "#2563eb", pin: "#2563eb" },
  { id: "pink",   label: "Розовый", bg: "rgba(244,114,182,0.28)", border: "#db2777", pin: "#db2777" },
  { id: "orange", label: "Оранж",   bg: "rgba(251,146,60,0.30)",  border: "#ea580c", pin: "#ea580c" },
];
const DEFAULT_COLOR = HIGHLIGHT_COLORS[0];
function getColorByContactId(contactId) {
  const idx = (Number(contactId) || 0) % HIGHLIGHT_COLORS.length;
  return HIGHLIGHT_COLORS[idx];
}

function getColor(colorId, contactId) {
  if (colorId) {
    const found = HIGHLIGHT_COLORS.find((c) => c.id === colorId);
    if (found) return found;
  }
  if (contactId) return getColorByContactId(contactId);
  return DEFAULT_COLOR;
}

/* ===================== helpers ===================== */
function normalizeStaffTab(path) {
  if (!path) return path;
  try {
    const url = new URL(path, window.location.origin);
    if (url.pathname === STAFF_ROUTE) {
      if (!url.searchParams.get("tab")) url.searchParams.set("tab", STAFF_TAB_KEY);
      if (!url.hash || url.hash === "#/" || url.hash === "#") url.hash = `#${STAFF_TAB_KEY}`;
      return url.pathname + url.search + url.hash;
    }
  } catch {}
  return path;
}

function normalizeFrom(input) {
  if (!input) return null;
  let s = String(input).trim();
  if (!s || s === "null" || s === "undefined") return null;
  try {
    const u = new URL(s, window.location.origin);
    s = (u.pathname || "") + (u.search || "") + (u.hash || "");
  } catch {}
  if (s.startsWith("#/")) s = s.slice(1);
  s = s.replace(/\/{2,}/g, "/");
  if (!s.startsWith("/")) s = "/" + s;
  return s;
}

function getToken() { return localStorage.getItem("token") || ""; }

function safeJsonParse(v, fallback = null) {
  if (!v) return fallback;
  try { return typeof v === "string" ? JSON.parse(v) : v; } catch { return fallback; }
}

function revokeObjectUrls(urls = []) {
  for (const url of urls) { try { if (url) URL.revokeObjectURL(url); } catch {} }
}

/* ===================== SelectionPopover ===================== */
function SelectionPopover({ locale, quote, image, onSave, onCancel }) {
  const [comment, setComment] = useState("");
  const [saving, setSaving]   = useState(false);
  const [colorId, setColorId] = useState(DEFAULT_COLOR.id);

  const handleSave = async () => {
    const clean = String(comment || "").trim();
    if (!clean || saving) return;
    try {
      setSaving(true);
      await onSave(clean, colorId);
    } finally {
      setSaving(false);
    }
  };

  const cm = getColor(colorId);

  return (
    <div style={{
      width: 340, background: "#fff", border: "1px solid #e5e7eb",
      borderRadius: 18, boxShadow: "0 20px 48px rgba(0,0,0,.14)", padding: 14,
    }}>
      {/* Head */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 900, color: "#111827" }}>
          {locale === "ru" ? "Новое замечание" : "Жаңа ескерту"}
        </div>
        <button onClick={onCancel} disabled={saving} style={{
          border: "none", background: "transparent",
          cursor: saving ? "default" : "pointer", color: "#9ca3af",
          fontSize: 20, lineHeight: 1, padding: "0 2px",
        }}>×</button>
      </div>

      {/* Fragment */}
      <div style={{ fontSize: 10.5, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>
        {locale === "ru" ? "Фрагмент" : "Үзінді"}
      </div>
      <div style={{
        fontSize: 12.5, color: "#374151", background: "#f9fafb",
        border: "1px solid #eef2f7", borderRadius: 10, padding: "8px 10px",
        maxHeight: 80, overflow: "auto", whiteSpace: "pre-wrap", lineHeight: 1.4, marginBottom: 10,
      }}>
        {quote || (locale === "ru"
          ? "— (без текста, Alt+мышь = скан) —"
          : "— (мәтін жоқ, Alt+тышқан = скан) —")}
      </div>

      {image && (
        <>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 }}>
            Скан
          </div>
          <img src={image} alt="selection preview" style={{
            width: "100%", borderRadius: 10, border: "1px solid #e5e7eb",
            display: "block", maxHeight: 160, objectFit: "contain", marginBottom: 10,
          }} />
        </>
      )}

      {/* Color picker */}
      <div style={{ fontSize: 10.5, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 7 }}>
        {locale === "ru" ? "Цвет подсветки" : "Бөлектеу түсі"}
      </div>
      <div style={{ display: "flex", gap: 7, alignItems: "center", marginBottom: 11 }}>
        {HIGHLIGHT_COLORS.map((c) => (
          <button
            key={c.id} type="button" title={c.label}
            onClick={() => setColorId(c.id)}
            style={{
              width: 26, height: 26, borderRadius: "50%",
              background: c.bg,
              border: colorId === c.id ? `2.5px solid ${c.border}` : "2px solid rgba(0,0,0,0.1)",
              cursor: "pointer", outline: "none", flexShrink: 0,
              transform: colorId === c.id ? "scale(1.18)" : "scale(1)",
              transition: "all .15s ease",
              boxShadow: colorId === c.id ? `0 0 0 3px ${c.border}33` : "none",
            }}
          />
        ))}
        <span style={{ fontSize: 11.5, color: "#64748b", marginLeft: 3, fontWeight: 500 }}>{cm.label}</span>
      </div>

      {/* Comment */}
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={3}
        placeholder={locale === "ru" ? "Опишите замечание…" : "Ескертуді жазыңыз…"}
        style={{
          width: "100%", resize: "vertical", borderRadius: 10,
          border: "1px solid #d1d5db", padding: "9px 11px",
          fontSize: 13.5, outline: "none", boxSizing: "border-box",
          fontFamily: "inherit", lineHeight: 1.45, color: "#111827",
        }}
      />

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
        <button onClick={onCancel} disabled={saving} style={{
          border: "1px solid #e5e7eb", background: "#f9fafb", borderRadius: 10,
          padding: "8px 13px", cursor: saving ? "default" : "pointer",
          fontWeight: 700, fontSize: 12.5, color: "#374151", opacity: saving ? 0.6 : 1,
        }}>
          {locale === "ru" ? "Отмена" : "Болдырмау"}
        </button>
        <button onClick={handleSave} disabled={saving} style={{
          border: "none", background: "#2563eb", color: "#fff",
          borderRadius: 10, padding: "8px 16px",
          cursor: saving ? "default" : "pointer",
          fontWeight: 700, fontSize: 12.5,
          boxShadow: "0 4px 14px rgba(37,99,235,.25)",
          opacity: saving ? 0.7 : 1,
        }}>
          {saving
            ? (locale === "ru" ? "Сохранение..." : "Сақталуда...")
            : (locale === "ru" ? "Сохранить" : "Сақтау")}
        </button>
      </div>
    </div>
  );
}

/* ===================== component ===================== */
export default function ErrorReviewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id }   = useParams();

  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const locale = useMemo(() => {
    const v = (
      qs.get("locale") || location.state?.locale ||
      localStorage.getItem("locale") || "kz"
    ).toLowerCase();
    return v === "ru" ? "ru" : "kz";
  }, [qs, location.state]);

  useEffect(() => { try { localStorage.setItem("locale", locale); } catch {} }, [locale]);



  const fromQuery = qs.get("from");
  const fromState = location.state?.from || null;
  const fromStore = sessionStorage.getItem("ee_last_list") || null;
  const roleAwareDefault = window.location.pathname.startsWith("/staff") ? "/staff-groups" : STAFF_URL;

  const referrerGuess = useMemo(() => {
    try {
      const r = new URL(document.referrer);
      if (r.origin === window.location.origin)
        return normalizeFrom((r.pathname || "") + (r.search || "") + (r.hash || ""));
    } catch {}
    return null;
  }, []);

  const from = useMemo(() => {
    const chosen =
      normalizeFrom(fromQuery) || normalizeFrom(fromState) ||
      normalizeFrom(fromStore) || normalizeFrom(referrerGuess) || roleAwareDefault;
    return normalizeStaffTab(chosen);
  }, [fromQuery, fromState, fromStore, referrerGuess, roleAwareDefault]);

  const goBack = useCallback(() => {
    const fs  = normalizeFrom(location.state?.from);
    const fss = normalizeFrom(sessionStorage.getItem("ee_last_list"));
    const f   = normalizeFrom(from);
    if (fs)  return navigate(normalizeStaffTab(fs),  { replace: true });
    if (fss) return navigate(normalizeStaffTab(fss), { replace: true });
    if (f)   return navigate(normalizeStaffTab(f),   { replace: true });
    window.history.back();
  }, [navigate, location.state, from]);

  /* ===================== state ===================== */
  const [pdfUrl, setPdfUrl]               = useState(null);
  const pdfUrlRef                          = useRef(null);
  const [pdfKey, setPdfKey]               = useState(0);
  const [selectedPart, setSelectedPart]   = useState(null);
  const [loadingInfo, setLoadingInfo]     = useState(false);
  const [loadingPdf, setLoadingPdf]       = useState(false);
  const [creatingError, setCreatingError] = useState(false);
  const [exportingWord, setExportingWord] = useState(false);
  const [parts, setParts]                 = useState([]);
  const [errors, setErrors]               = useState([]);
  const [errorReviewData, setErrorReviewData] = useState(null);
  const [newError, setNewError]           = useState({ comment: "", page: "" });
  const [screenshotBlobMap, setScreenshotBlobMap] = useState({});
  const scrollToRef  = useRef(null);
  const fileInputRef = useRef(null);
const [fileName, setFileName]     = useState("");
const [uploadingFile, setUploadingFile]     = useState(false);
const [downloadingArchive, setDownloadingArchive] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [scope, setScope] = useState("all"); // "all" | "mine" | "others"
  const [editingId, setEditingId]   = useState(null);
const [editDraft, setEditDraft]   = useState({ comment: "", page: "" });
const [menuOpenId, setMenuOpenId] = useState(null);
const [deletingId, setDeletingId] = useState(null);
const [savingId, setSavingId] = useState(null);
const [chatMessages, setChatMessages]   = useState([]);
const [chatInput, setChatInput]         = useState("");
const [pinnedRemark, setPinnedRemark]   = useState(null);
const [replyingTo, setReplyingTo] = useState(null); // { id, text, author }
const [sendingChat, setSendingChat]   = useState(false);
const [loadingChat, setLoadingChat]   = useState(false);
const [currentUserId, setCurrentUserId] = useState(null);
const [currentUserRole, setCurrentUserRole] = useState(null);
const isReadOnly = useMemo(() => {
  return currentUserRole === "internal_expert";
}, [currentUserRole]);
const chatScrollRef = useRef(null);
const [showScrollBtn, setShowScrollBtn] = useState(false);
const msgRefsMap = useRef({});
const notificationSoundRef = useRef(null);

const playNotificationSound = useCallback(() => {
  try {
    if (!notificationSoundRef.current) {
      notificationSoundRef.current = new Audio("/sounds/notification.mp3");
      notificationSoundRef.current.volume = 0.5;
    }
    notificationSoundRef.current.currentTime = 0;
    notificationSoundRef.current.play().catch(() => {});
  } catch {}
}, []);
const [leftWidth, setLeftWidth]     = useState(340);
const [infoHeight, setInfoHeight]   = useState(260);
const isResizingRef                  = useRef(false);
const startXRef                      = useRef(0);
const startWidthRef                  = useRef(340);
const isResizingYRef                 = useRef(false);
const startYRef                      = useRef(0);
const startHeightRef                 = useRef(260);
  const pdfAbortRef        = useRef(null);
  const infoAbortRef       = useRef(null);
  const errorsAbortRef     = useRef(null);
  const screenshotAbortRef = useRef(null);

  /* ===================== styles ===================== */
  const S = {
shell: {
  display: "grid",
  gridTemplateColumns: `${leftWidth}px 8px minmax(0, 1fr) 420px`,
      gap: 14, padding: 14,
      height: "100vh", width: "100vw",
      boxSizing: "border-box", overflow: "hidden",
      background: "#f1f4f9",
      fontFamily: "'Segoe UI', system-ui, sans-serif",
    },
    col: { minHeight: 0, minWidth: 0, display: "flex", flexDirection: "column", gap: 14 },
    card: {
      minWidth: 0, background: "#fff",
      border: "1px solid rgba(15,23,42,0.08)", borderRadius: 20, overflow: "hidden",
      boxShadow: "0 2px 10px rgba(15,23,42,0.06), 0 1px 3px rgba(15,23,42,0.04)",
      display: "flex", flexDirection: "column", minHeight: 0,
    },
    header: {
      padding: "13px 16px", borderBottom: "1px solid rgba(15,23,42,0.06)",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: 10, background: "#fff", minWidth: 0, flexShrink: 0,
    },
    title: { fontSize: 13, fontWeight: 800, color: "#0f172a" },
    muted: { fontSize: 11.5, color: "#94a3b8", marginTop: 2 },
    body:  { padding: 14, minHeight: 0, minWidth: 0 },
    scroll: { overflow: "auto", minHeight: 0, minWidth: 0 },
    btnBack: {
      border: "1px solid rgba(15,23,42,0.1)", background: "#f8fafc",
      color: "#334155", borderRadius: 10, padding: "8px 13px",
      cursor: "pointer", fontWeight: 700, fontSize: 12.5,
      whiteSpace: "nowrap", flexShrink: 0,
    },
    btnSuccess: {
      width: "100%", border: "none", background: "#16a34a", color: "#fff",
      borderRadius: 12, padding: "11px 12px", cursor: "pointer",
      fontWeight: 700, fontSize: 13, boxShadow: "0 4px 14px rgba(22,163,74,0.25)",
    },
    btnWarn: {
      width: "100%", border: "none", background: "#d97706", color: "#fff",
      borderRadius: 12, padding: "11px 12px", cursor: "pointer",
      fontWeight: 700, fontSize: 13, boxShadow: "0 4px 14px rgba(217,119,6,0.22)",
    },
    btnDocx: {
      width: "100%",
      border: "1.5px solid #2563eb",
      background: "#fff",
      color: "#2563eb",
      borderRadius: 12,
      padding: "10px 12px",
      cursor: "pointer",
      fontWeight: 700,
      fontSize: 13,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 7,
      transition: "all .18s ease",
    },
    input: {
      width: "100%", padding: "9px 11px", fontSize: 13,
      border: "1px solid rgba(15,23,42,0.12)", borderRadius: 10,
      backgroundColor: "#f8fafc", outline: "none",
      boxSizing: "border-box", fontFamily: "inherit", color: "#0f172a",
    },
    textarea: {
      width: "100%", padding: "9px 11px", fontSize: 13,
      border: "1px solid rgba(15,23,42,0.12)", borderRadius: 10,
      backgroundColor: "#f8fafc", outline: "none",
      resize: "vertical", boxSizing: "border-box",
      fontFamily: "inherit", color: "#0f172a",
    },
    select: {
      padding: "7px 26px 7px 10px", borderRadius: 10,
      border: "1px solid rgba(15,23,42,0.12)", fontSize: 12.5,
      background: "#f8fafc", outline: "none", maxWidth: 230,
      color: "#0f172a", cursor: "pointer",
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath fill='%2394a3b8' d='M5 7L1 3h8z'/%3E%3C/svg%3E\")",
      backgroundRepeat: "no-repeat", backgroundPosition: "right 9px center",
      WebkitAppearance: "none",
    },
    metaBox: {
      border: "1px solid rgba(15,23,42,0.07)", background: "#f8fafc",
      borderRadius: 12, overflow: "hidden",
    },
    metaRow: {
      display: "grid", gridTemplateColumns: "100px 1fr", gap: 8,
      padding: "9px 12px", borderBottom: "1px solid rgba(15,23,42,0.05)",
    },
    metaKey: { fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", paddingTop: 1 },
    metaVal: { fontSize: 13, fontWeight: 600, color: "#0f172a" },
    hint: {
      fontSize: 12, color: "#64748b", background: "#f8fafc",
      border: "1px solid rgba(15,23,42,0.07)", borderRadius: 12,
      padding: "11px 13px", lineHeight: 1.5,
    },
    viewerWrap: {
      flex: 1, minHeight: 0, minWidth: 0, overflow: "hidden",
      position: "relative", background: "#dde1e9", contain: "layout paint",
    },
    list: {
      flex: 1, minHeight: 0, minWidth: 0, overflow: "auto",
      padding: 12, display: "flex", flexDirection: "column", gap: 8, background: "#fff",
    },
    uploadRow: { display: "grid", gridTemplateColumns: "130px 1fr", gap: 8, alignItems: "center" },
    uploadBtn: {
      border: "1px solid rgba(15,23,42,0.1)", background: "#f8fafc",
      borderRadius: 10, padding: "8px 11px", cursor: "pointer",
      fontWeight: 700, fontSize: 12, color: "#334155",
    },
    fileName: {
      fontSize: 12, color: "#94a3b8",
      border: "1px solid rgba(15,23,42,0.07)", background: "#f8fafc",
      borderRadius: 10, padding: "8px 11px",
      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    },
    imagePlaceholder: {
      marginTop: 8, width: "100%", minHeight: 66, borderRadius: 10,
      border: "1px dashed rgba(15,23,42,0.12)", background: "#f8fafc",
      display: "grid", placeItems: "center", color: "#94a3b8",
      fontSize: 12, fontWeight: 600,
    },
  };

  /* ===================== cleanup ===================== */
  const revokePdfUrl = useCallback((url) => { try { if (url) URL.revokeObjectURL(url); } catch {} }, []);

  useEffect(() => {
    return () => {
      if (pdfAbortRef.current) pdfAbortRef.current.abort();
      if (infoAbortRef.current) infoAbortRef.current.abort();
      if (errorsAbortRef.current) errorsAbortRef.current.abort();
      if (screenshotAbortRef.current) screenshotAbortRef.current.abort();
      revokePdfUrl(pdfUrlRef.current);
    };
  }, [revokePdfUrl]);

  /* ===================== fetch info ===================== */
  useEffect(() => {
    if (!id) return;
    if (infoAbortRef.current) infoAbortRef.current.abort();
    const controller = new AbortController();
    infoAbortRef.current = controller;
    setLoadingInfo(true);

    (async () => {
      try {
        const res = await api.get(
  `/expertise/external/applications/${id}/error-review`,
  { signal: controller.signal }
);
        const data = res.data || null;
        setErrorReviewData(data);
        const mapped = (data?.files || []).map((f) => ({
  id: Number(f.id),
  label: f.application_file_name || (locale === "ru" ? `Раздел ${f.section}` : `Бөлім ${f.section}`),
  pdfUrl: f.file_path ? `${API_BASE}/api/expertise/internal-files/${f.id}/pdf` : null,
  fileLink: f.file_link ?? null,
  archiveUrl: f.section_archive_download_url ?? null,   // ← добавить
  archiveName: f.section_archive_name ?? null,          // ← добавить
}));
        setParts(mapped);
        setSelectedPart((prev) => mapped.some((p) => p.id === prev) ? prev : mapped[0]?.id ?? null);
        setErrors([]); setActiveId(null);
      } catch (e) {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
        alert(locale === "ru" ? "Не удалось загрузить разделы." : "Бөліктерді жүктеу мүмкін болмады.");
        setParts([]); setSelectedPart(null); setErrorReviewData(null);
      } finally { setLoadingInfo(false); }
    })();
  }, [id, locale]);

  /* ===================== fetch current user ===================== */
useEffect(() => {
  (async () => {
    try {
      const res = await api.get(`/internal/profile`);
      setCurrentUserId(res.data?.id || res.data?.user?.id || null);
      setCurrentUserRole(res.data?.roles?.[0] || null);
    } catch {}
  })();
}, []);

  /* ===================== load errors ===================== */
  useEffect(() => {
    if (!id || !selectedPart) return;
    if (errorsAbortRef.current) errorsAbortRef.current.abort();
    const controller = new AbortController();
    errorsAbortRef.current = controller;

    (async () => {
      try {
        const params = new URLSearchParams({ book: Number(id), scope });
const res = await api.get(
  `/expertise/book-errors?${params}`,
  { signal: controller.signal }
);


        const loaded = (res.data?.results || [])
  .filter((er) => Number(er.file_id) === Number(selectedPart))
  .map((er) => ({
    ...er,
    highlight_color: er.highlight_color || DEFAULT_COLOR.id,
  }));

        setErrors(loaded);
        setActiveId(null);
      } catch (e) {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
        setErrors([]);
      }
    })();
  }, [id, selectedPart, scope]);

  /* ===================== load screenshots ===================== */
  useEffect(() => {
    if (screenshotAbortRef.current) screenshotAbortRef.current.abort();
    const withScreens = errors.filter((er) => !!er?.screenshot_url);
    if (withScreens.length === 0) { setScreenshotBlobMap({}); return; }

    const controller = new AbortController();
    screenshotAbortRef.current = controller;
    let cancelled = false;
    const created = [];

    (async () => {
  const nextMap = {};
  await Promise.all(withScreens.map(async (er) => {
    try {
      const res = await axios.get(er.screenshot_url, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${getToken()}` },
        signal: controller.signal,
      });
      const u = URL.createObjectURL(res.data);
      created.push(u);
      nextMap[String(er.id)] = u;
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
    }
  }));
  if (cancelled) { revokeObjectUrls(created); return; }
  setScreenshotBlobMap(nextMap);
})();

    return () => { cancelled = true; controller.abort(); revokeObjectUrls(created); };
  }, [errors]);

  /* ===================== load PDF ===================== */
  useEffect(() => {
    if (!selectedPart) {
      setPdfUrl((prev) => { revokePdfUrl(prev); pdfUrlRef.current = null; return null; }); return;
    }
    const part = parts.find((p) => Number(p.id) === Number(selectedPart));
    if (!part) return;

    if (pdfAbortRef.current) pdfAbortRef.current.abort();
    const controller = new AbortController();
    pdfAbortRef.current = controller;

    (async () => {
      if (!part.pdfUrl) {
        setPdfUrl((prev) => { revokePdfUrl(prev); pdfUrlRef.current = null; return null; });
        setPdfKey((k) => k + 1); return;
      }
      setLoadingPdf(true);
      try {
        const resp = await axios.get(part.pdfUrl, {
          responseType: "blob",
          headers: { Authorization: `Bearer ${getToken()}` },
          signal: controller.signal,
        });
        const blobUrl = URL.createObjectURL(resp.data);
        setPdfUrl((prev) => {
  if (prev && prev !== blobUrl) {
    setTimeout(() => revokePdfUrl(prev), 3000);
  }

  pdfUrlRef.current = blobUrl;
  return blobUrl;
});
        setPdfKey((k) => k + 1);
      } catch (e) {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
        setPdfUrl((prev) => { revokePdfUrl(prev); pdfUrlRef.current = null; return null; });
      } finally { setLoadingPdf(false); }
    })();
  }, [selectedPart, parts, revokePdfUrl]);



  /* ===================== reset scroll ===================== */
  useEffect(() => {
    if (!pdfUrl) return;
    let r1 = 0, r2 = 0;
    r1 = requestAnimationFrame(() => {
      r2 = requestAnimationFrame(() => {
        const el = document.querySelector(".ErrorReviewPage .PdfHighlighter__viewer");
        if (el) { el.scrollTop = 0; el.scrollLeft = 0; }
      });
    });
    return () => { cancelAnimationFrame(r1); cancelAnimationFrame(r2); };
  }, [pdfUrl, pdfKey]);

  const handleResizeYStart = useCallback((e) => {
  isResizingYRef.current = true;
  startYRef.current = e.clientY;
  startHeightRef.current = infoHeight;
  document.body.style.cursor = "row-resize";
  document.body.style.userSelect = "none";

  const onMove = (ev) => {
    if (!isResizingYRef.current) return;
    const delta = ev.clientY - startYRef.current;
    const newH = Math.min(480, Math.max(120, startHeightRef.current + delta));
    setInfoHeight(newH);
  };

  const onUp = () => {
    isResizingYRef.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    document.removeEventListener("mousemove", onMove);
    document.removeEventListener("mouseup", onUp);
  };

  document.addEventListener("mousemove", onMove);
  document.addEventListener("mouseup", onUp);
}, [infoHeight]);

  const handleResizeStart = useCallback((e) => {
  isResizingRef.current = true;
  startXRef.current = e.clientX;
  startWidthRef.current = leftWidth;

  const onMove = (ev) => {
    if (!isResizingRef.current) return;
    const delta = ev.clientX - startXRef.current;
    const newWidth = Math.min(600, Math.max(240, startWidthRef.current + delta));
    setLeftWidth(newWidth);
  };

  const onUp = () => {
    isResizingRef.current = false;
    document.removeEventListener("mousemove", onMove);
    document.removeEventListener("mouseup", onUp);
  };

  document.addEventListener("mousemove", onMove);
  document.addEventListener("mouseup", onUp);
}, [leftWidth]);

  /* ===================== handlers ===================== */
  const handlePartChange = useCallback((e) => {
    const v = Number(e.target.value);
    setSelectedPart(Number.isFinite(v) ? v : null);
  }, []);

const handleUploadErrorFile = useCallback(async (file) => {
  if (!file || !id) return;
  try {
    setUploadingFile(true);
    const form = new FormData(); form.append("file", file);
    await api.post(
  `/expertise/external/applications/${id}/error-file`,
  form,
  { headers: { "Content-Type": "multipart/form-data" } }
);
    alert(locale === "ru" ? "📎 Файл сохранён!" : "📎 Файл сақталды!");
  } catch (err) {
    alert(locale === "ru" ? "❌ Не удалось загрузить файл." : "❌ Файлды жүктеу мүмкін болмады.");
  } finally {
    setUploadingFile(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }
}, [id, locale]);

const handleDownloadArchive = useCallback(async (url, name) => {
  if (!url || downloadingArchive) return;
  try {
    setDownloadingArchive(true);
    const resp = await axios.get(url, {
  responseType: "blob",
  // НЕ передаём Authorization — pre-signed URL уже содержит подпись
});
    const blobUrl = URL.createObjectURL(new Blob([resp.data]));
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = name || "archive.zip";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    alert(locale === "ru" ? "❌ Не удалось скачать архив." : "❌ Архивті жүктеу мүмкін болмады.");
  } finally {
    setDownloadingArchive(false);
  }
}, [downloadingArchive, locale]);

  const handleFinishErrorReview = useCallback(async () => {
    if (!id) return;
    try {
      await api.post(`/expertise/external/applications/${id}/error-review/finish`, {});
      alert(locale === "ru" ? "✅ Этап проверки ошибок завершён!" : "✅ Қателерді анықтау кезеңі аяқталды!");
      goBack();
    } catch (err) {
      alert(locale === "ru"
        ? "❌ Не удалось завершить этап проверки ошибок."
        : "❌ Қателерді анықтау кезеңін аяқтау мүмкін болмады.");
    }
  }, [id, locale, goBack]);

  /* ===================== export word ===================== */
  const handleExportWord = useCallback(async () => {
    if (!id || exportingWord) return;
    try {
      setExportingWord(true);

      const params = new URLSearchParams({ locale, scope });
if (selectedPart) params.set("file_id", String(selectedPart));

      const response = await api.get(
  `/expertise/book-errors/${id}/export-word?${params}`,
  { responseType: "blob" }
);

      // Достаём имя файла из заголовка или генерируем сами
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename[^;=\n]*=(['"]?)([^\n'"]+)\1/);
      const filename = match ? match[2] : `remarks_book_${id}.docx`;

      const url = URL.createObjectURL(new Blob([response.data]));
      const a   = document.createElement("a");
      a.href    = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(
        locale === "ru"
          ? "❌ Не удалось создать файл замечаний."
          : "❌ Ескертулер файлын жасау мүмкін болмады."
      );
    } finally {
      setExportingWord(false);
    }
  }, [id, locale, selectedPart, exportingWord, scope]);

  const createError = useCallback(async ({
  comment, page, quads, screenshotBase64 = null, colorId = DEFAULT_COLOR.id, selectedText = "",
}) => {
    const part = parts.find((p) => Number(p.id) === Number(selectedPart));
    if (!part) return false;

    const cleanComment = String(comment || "").trim();
    const pageNum = Number(page);

    if (!cleanComment) {
      alert(locale === "ru" ? "Введите текст замечания." : "Ескерту мәтінін енгізіңіз.");
      return false;
    }
    if (!Number.isFinite(pageNum) || pageNum <= 0) {
      alert(locale === "ru" ? "Укажите корректную страницу." : "Дұрыс бетті көрсетіңіз.");
      return false;
    }

    try {
      setCreatingError(true);
      const payload = {
  book: Number(id), file: Number(part.id),
  author: locale === "ru" ? "external_expert" : "Сыртқы сарапшы",
  comment: cleanComment, page: pageNum,
  quads: JSON.stringify(quads ?? []),
  selected_text: selectedText || "",
  screenshot_base64: screenshotBase64,
  error_type: null, is_final_error: false,
  highlight_color: colorId || DEFAULT_COLOR.id,
};

      const res = await api.post(`/expertise/book-errors`, payload);

      const newId = res.data?.id ?? `${Date.now()}-${Math.random()}`;

      try {
        const saved = JSON.parse(localStorage.getItem(`hl_colors_${id}`) || "{}");
        saved[String(newId)] = colorId;
        localStorage.setItem(`hl_colors_${id}`, JSON.stringify(saved));
      } catch {}

      setErrors((prev) => [...prev, {
  id: newId, book_id: Number(id), file_id: Number(part.id),
  author: res.data?.author || payload.author,
  author_display: res.data?.author_display || payload.author,
  is_mine: true,
  contact_id: res.data?.contact_id || null,
  comment: payload.comment, page: payload.page,
  quads: payload.quads, error_type: null, is_final_error: false,
  screenshot_url: res.data?.screenshot_url || null,
  highlight_color: res.data?.highlight_color || colorId,
  can_edit: true,
  can_delete: true,
}]);
      setNewError({ comment: "", page: "" });
      return true;
    } catch (err) {
      alert("❌ " + (err.response?.data?.message || err.message));
      return false;
    } finally { setCreatingError(false); }
  }, [parts, selectedPart, id, locale]);

  const handleAddErrorManual = useCallback(async () => {
    await createError({ comment: newError.comment, page: newError.page, quads: [], screenshotBase64: null });
  }, [createError, newError]);

  const handleDeleteError = useCallback(async (erId) => {
  if (!window.confirm(locale === "ru" ? "Удалить замечание?" : "Ескертуді жою керек пе?")) return;
  try {
    setDeletingId(erId);
    await api.delete(`/expertise/book-errors/${erId}`);
    setErrors((prev) => prev.filter((e) => String(e.id) !== String(erId)));
    if (String(activeId) === String(erId)) setActiveId(null);
  } catch (err) {
    alert("❌ " + (err.response?.data?.message || err.message));
  } finally {
    setDeletingId(null);
    setMenuOpenId(null);
  }
}, [locale, activeId]);

const handleStartEdit = useCallback((er) => {
  setEditingId(String(er.id));
  setEditDraft({ comment: er.comment || "", page: String(er.page || "") });
  setMenuOpenId(null);
}, []);

const handleCancelEdit = useCallback(() => {
  setEditingId(null);
  setEditDraft({ comment: "", page: "" });
}, []);

const handlePinToChat = useCallback((er) => {
  setPinnedRemark({
    id: er.id,
    page: er.page,
    comment: er.comment,
    selected_text: er.selected_text || "",
    highlight_color: er.highlight_color,
    contact_id: er.contact_id,
    author_display: er.author_display,
    file_id: er.file_id,
  });
  setTimeout(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, 100);
}, []);

// нормализуем сообщение из API в наш формат
const normalizeChatMsg = useCallback((msg) => {
  // если бэк уже вернул remark — используем
  let remark = msg.remark || null;
  let text = msg.body || "";


if (text) {
  const match = text.match(/^\[📌\s*(?:Бет|Стр\.)\s*(\d+):\s*([^\]]+)\]/);
  if (match) {
    if (!remark) {
      remark = {
        id: msg.remark_id || null,
        page: Number(match[1]),
        comment: match[2].trim(),
        selected_text: "",
        highlight_color: null,
        author_display: null,
      };
    }
    text = text.replace(match[0], "").trim(); // ← чистим текст в любом случае
  }
}

  return {
    id: msg.id,
    author_display: msg.sender_name || (locale === "ru" ? "Эксперт" : "Сарапшы"),
    text,
    remark,
    created_at: msg.created_at || new Date().toISOString(),
    is_mine: currentUserId ? String(msg.sender_id) === String(currentUserId) : false,
    page_number: msg.page_number || null,
    sender_id: msg.sender_id,
  };
}, [locale, currentUserId]);

// загрузить историю
const loadChatMessages = useCallback(async () => {
  if (!id) return;
  try {
    setLoadingChat(true);
    const res = await api.get(
  `/expertise/applications/${id}/chat/messages`,
  { params: { limit: 80 } }
);
    const msgs = (res.data?.data || []).map((m) => normalizeChatMsg(m));
    setChatMessages(msgs);
    setTimeout(() => {
      if (chatScrollRef.current) {
        chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
      }
    }, 50);
  } catch (e) {
  } finally {
    setLoadingChat(false);
  }
}, [id, normalizeChatMsg]);

// отправить сообщение
const handleSendChat = useCallback(async () => {
  const text = String(chatInput || "").trim();
  if (!text && !pinnedRemark) return;

  // формируем текст — если есть прикреплённое замечание, добавляем ссылку
  let body = text;
if (replyingTo) {
  const quote = `> ${replyingTo.author}: ${replyingTo.text?.slice(0, 80)}`;
  body = text ? `${quote}\n${text}` : quote;
}
if (pinnedRemark) {
  const ref = `[📌 ${locale === "ru" ? "Стр." : "Бет"} ${pinnedRemark.page}: ${pinnedRemark.comment?.slice(0, 60)}]`;
  body = body ? `${ref}\n${body}` : ref;
}


const tempMsg = {
  id: `temp-${Date.now()}`,
  author_display: locale === "ru" ? "Вы" : "Сіз",
  text: text,   // ← только то что юзер написал, без [📌...]
  remark: pinnedRemark ? { ...pinnedRemark } : null,
  created_at: new Date().toISOString(),
  is_mine: true,
  page_number: pinnedRemark?.page || null,
};

  setChatMessages((prev) => [...prev, tempMsg]);
setChatInput("");
setPinnedRemark(null);
setReplyingTo(null);
  setTimeout(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, 50);

  try {
    setSendingChat(true);
    await api.post(
  `/expertise/applications/${id}/chat/messages`,
  {
    body,
    application_file_id: pinnedRemark?.file_id || selectedPart || null,
    section_number: null,
    page_number: pinnedRemark?.page || null,
    remark_id: pinnedRemark?.id || null,
    reply_to_id: replyingTo?.id || null,
  }
);
  } catch (err) {
    // убираем оптимистичное сообщение если ошибка
    setChatMessages((prev) => prev.filter((m) => m.id !== tempMsg.id));
    alert("❌ " + (err.response?.data?.message || err.message));
  } finally {
    setSendingChat(false);
  }
}, [chatInput, pinnedRemark, id, locale, selectedPart]);

  /* ===================== chat init ===================== */
useEffect(() => {
  loadChatMessages();
}, [loadChatMessages]);

useEffect(() => {
  if (!id) return;

  const echo = getEcho();
  if (!echo) return;

  const channel = echo.private(`application.${id}.chat`);


channel.listen(".chat.message.created", (event) => {
  const msg = event?.message || event;
  if (!msg || !msg.id) return;

  const normalized = normalizeChatMsg(msg);

  // звук ДО setState — вне чистой функции
  if (!normalized.is_mine) {
    playNotificationSound();
  }

  setChatMessages((prev) => {
    if (prev.some((m) => String(m.id) === String(normalized.id))) return prev;

    const isFromMe = currentUserId && String(msg.sender_id) === String(currentUserId);

    if (isFromMe) {
      const tempIdx = prev.findIndex((m) => String(m.id).startsWith("temp-"));
      if (tempIdx !== -1) {
        const next = [...prev];
        next[tempIdx] = { ...normalized, is_mine: true };
        return next;
      }
    }

    return [...prev, normalized];
  });

  setTimeout(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, 50);
});

  return () => {
    echo.leave(`application.${id}.chat`);
  };
}, [id, normalizeChatMsg]);

useEffect(() => {
  return () => disconnectEcho();
}, []);



const handleSaveEdit = useCallback(async (erId) => {
const clean = String(editDraft.comment || "").trim();
if (!clean) return alert(locale === "ru" ? "Введите текст." : "Мәтін енгізіңіз.");
try {
  setSavingId(erId);
  await api.patch(`/expertise/book-errors/${erId}`, { comment: clean });
    setErrors((prev) => prev.map((e) =>
  String(e.id) === String(erId) ? { ...e, comment: clean } : e
));
    setEditingId(null);
  } catch (err) {
    alert("❌ " + (err.response?.data?.message || err.message));
  } finally {
    setSavingId(null);
  }
}, [editDraft, locale]);

  /* ===================== highlights ===================== */
  const highlights = useMemo(() =>
    errors.map((er) => {
      const pos = safeJsonParse(er.quads, null);
      if (!pos || !pos.pageNumber) return null;
      return {
        id: String(er.id), position: pos,
        content: { text: "", image: !!er.screenshot_url ? "scan" : null },
        comment: { text: er.comment || "" },
        __raw: er,
      };
    }).filter(Boolean),
  [errors]);

  const highlightById = useMemo(() => {
    const m = new Map();
    for (const h of highlights) m.set(String(h.__raw?.id), h);
    return m;
  }, [highlights]);

  const jumpToError = useCallback((er) => {
    const h = highlightById.get(String(er.id));
    if (!h || !scrollToRef.current) return;
    setActiveId(String(er.id)); scrollToRef.current(h);
  }, [highlightById]);

  const current = useMemo(
    () => parts.find((p) => Number(p.id) === Number(selectedPart)),
    [parts, selectedPart]
  );

  const highlightCss = useMemo(() => {
    const reset = `
      .ErrorReviewPage .Highlight__part {
        background: transparent !important;
        border-bottom: none !important;
      }
      .ErrorReviewPage .AreaHighlight__part {
        background: transparent !important;
        border: none !important;
      }
    `;
    const perItem = errors.map((er) => {
      const cm = getColor(er?.highlight_color, er?.contact_id);
      return `
        [data-hl-id="${er.id}"] .Highlight__part {
          background: ${cm.bg} !important;
          border-bottom: 2px solid ${cm.border} !important;
          border-radius: 2px !important;
        }
        [data-hl-id="${er.id}"] .AreaHighlight__part {
          background: ${cm.bg} !important;
          border: 2px solid ${cm.border} !important;
          border-radius: 4px !important;
        }
      `;
    }).join("\n");
    return reset + perItem;
  }, [errors]);

  /* ===================== render ===================== */
  return (
    <div className="ErrorReviewPage" style={S.shell}>
      <style>{`
        .ErrorReviewPage, .ErrorReviewPage * { box-sizing: border-box; }

        .ErrorReviewPage .PdfHighlighter,
        .ErrorReviewPage .PdfHighlighter__viewer {
          width: 100% !important; min-width: 0 !important; height: 100% !important;
        }
        .ErrorReviewPage .PdfHighlighter__viewer {
          position: relative !important; overflow-y: auto !important; overflow-x: auto !important;
          background: #dde1e9;
          scrollbar-width: thin; scrollbar-color: #cbd5e1 transparent;
        }
        .ErrorReviewPage .PdfHighlighter__viewer::-webkit-scrollbar { width: 5px; }
        .ErrorReviewPage .PdfHighlighter__viewer::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 99px; }
        .ErrorReviewPage .PdfHighlighter__tip-container { z-index: 9999 !important; }
        .ErrorReviewPage .PdfHighlighter__viewer .pdfViewer,
        .ErrorReviewPage .PdfHighlighter__viewer .pdfViewer.removePageBorders {
          max-width: 100% !important; overflow: hidden !important;
        }
        .ErrorReviewPage .PdfHighlighter__page,
        .ErrorReviewPage .PdfHighlighter__viewer .page { margin: 0 auto 12px !important; }

        .erp-remarks::-webkit-scrollbar { width: 4px; }
        .erp-remarks::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 99px; }
        .erp-body-scroll::-webkit-scrollbar { width: 4px; }
        .erp-body-scroll::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 99px; }

        @keyframes erp-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

.erp-menu-backdrop {
  position: fixed; inset: 0; z-index: 998;
}

.erp-chat-msg-wrap:hover .erp-reply-btn {
  opacity: 1 !important;
}
.erp-msg-bubble {
  position: relative;
}
.erp-msg-bubble:hover .erp-reply-btn {
  opacity: 1 !important;
}

        @keyframes scanPulse {
          0%   { box-shadow: 0 0 0 0 rgba(220,38,38,.35); }
          70%  { box-shadow: 0 0 0 10px rgba(220,38,38,0); }
          100% { box-shadow: 0 0 0 0 rgba(220,38,38,0); }
        }

        @keyframes erp-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
      <style>{highlightCss}</style>

      {/* ═══ LEFT ═══ */}
      <div style={S.col}>
        <div style={{ ...S.card, height: infoHeight, flexShrink: 0 }}>
          <div style={S.header}>
  <div style={{ minWidth: 0 }}>
    <div style={S.title}>{locale === "ru" ? "Информация" : "Ақпарат"}</div>
    <div style={S.muted}>{locale === "ru" ? "Книга / экспертиза" : "Кітап / сараптама"}</div>
  </div>
  <button onClick={goBack} style={S.btnBack}>
    {locale === "ru" ? "← Назад" : "← Артқа"}
  </button>
</div>

          <div className="erp-body-scroll" style={{ ...S.body, ...S.scroll, display: "flex", flexDirection: "column", gap: 12 }}>
            {loadingInfo && !errorReviewData ? (
              <div style={S.hint}>{locale === "ru" ? "Загрузка данных…" : "Деректер жүктелуде…"}</div>
            ) : errorReviewData ? (
              <div style={S.metaBox}>
                {[
                  [locale === "ru" ? "Предмет" : "Пән",  errorReviewData.subject],
                  [locale === "ru" ? "Класс"   : "Сынып", errorReviewData.grade],
                  [locale === "ru" ? "Язык"    : "Тіл",   errorReviewData.teaching_language],
                ].map(([key, val], i, arr) => (
                  <div key={key} style={{ ...S.metaRow, ...(i === arr.length - 1 ? { borderBottom: "none" } : {}) }}>
                    <div style={S.metaKey}>{key}</div>
                    <div style={S.metaVal}>{val ?? "—"}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={S.hint}>{locale === "ru" ? "Данные не найдены." : "Деректер табылмады."}</div>
            )}

            {errorReviewData?.word && (
  <div>
    <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
      {locale === "ru" ? "📝 Файл доработки" : "📝 Доработка файлы"}
    </div>
    <button
      type="button"
      style={{
        ...S.btnDocx,
        borderColor: "#16a34a",
        color: "#16a34a",
      }}
      onClick={() => {
        const wordName = errorReviewData.word?.original_name || "document.docx";
        fetch(`${API_BASE}/api/applications/${id}/download-word`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        })
          .then(r => r.blob())
          .then(blob => {
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = wordName;
            a.click();
            URL.revokeObjectURL(a.href);
          })
          .catch(() => alert(locale === "ru" ? "❌ Не удалось скачать" : "❌ Жүктеу мүмкін болмады"));
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="12" y1="11" x2="12" y2="17"/>
        <polyline points="9 14 12 17 15 14"/>
      </svg>
      {errorReviewData.word?.original_name || "document.docx"}
    </button>
  </div>
)}

{!isReadOnly && (
  <div>
    <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
      {locale === "ru" ? "📎 Файл замечаний" : "📎 Ескертулер файлы"}
    </div>
    <input ref={fileInputRef} type="file" accept=".docx" style={{ display: "none" }}
      onChange={(e) => { const f = e.target.files?.[0]; setFileName(f?.name || ""); if (f) handleUploadErrorFile(f); }}
    />
    <div style={S.uploadRow}>
      <button
        type="button"
        style={{
          ...S.uploadBtn,
          opacity: uploadingFile ? 0.7 : 1,
          cursor: uploadingFile ? "default" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        }}
        disabled={uploadingFile}
        onClick={() => !uploadingFile && fileInputRef.current?.click()}
      >
        {uploadingFile ? (
          <>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
              style={{ animation: "erp-spin 0.9s linear infinite", flexShrink: 0 }}>
              <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
            </svg>
            {locale === "ru" ? "Загрузка…" : "Жүктелуде…"}
          </>
        ) : (
          locale === "ru" ? "Выбрать файл" : "Файл таңдау"
        )}
      </button>
      <div style={{
        ...S.fileName,
        ...(uploadingFile ? { color: "#2563eb", borderColor: "rgba(37,99,235,0.2)", background: "rgba(37,99,235,0.04)" } : {}),
      }} title={fileName}>
        {uploadingFile
          ? (locale === "ru" ? "Отправка на сервер…" : "Серверге жіберілуде…")
          : fileName || (locale === "ru" ? "Файл не выбран" : "Файл таңдалмады")}
      </div>
    </div>
  </div>
)}

            {/* ── Кнопка экспорта Word ── */}
            <button
              onClick={handleExportWord}
              disabled={exportingWord || errors.length === 0}
              style={{
                ...S.btnDocx,
                opacity: (exportingWord || errors.length === 0) ? 0.45 : 1,
                cursor: (exportingWord || errors.length === 0) ? "default" : "pointer",
              }}
              title={
                errors.length === 0
                  ? (locale === "ru" ? "Нет замечаний для экспорта" : "Экспорттауға ескертулер жоқ")
                  : (locale === "ru" ? "Скачать все замечания в Word" : "Барлық ескертулерді Word-қа жүктеу")
              }
            >
              {exportingWord ? (
                <>
                  <svg
                    width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                    style={{ animation: "erp-spin 0.9s linear infinite", flexShrink: 0 }}
                  >
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                  </svg>
                  {locale === "ru" ? "Формирование…" : "Жасалуда…"}
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="12" y1="11" x2="12" y2="17"/>
                    <polyline points="9 14 12 17 15 14"/>
                  </svg>
                  {locale === "ru" ? "Скачать замечания (.docx)" : "Ескертулерді жүктеу (.docx)"}
                </>
              )}
            </button>

           {!isReadOnly && (
              <button onClick={handleFinishErrorReview} style={S.btnSuccess}>
                {locale === "ru" ? "✅ Завершить проверку" : "✅ Тексеруді аяқтау"}
              </button>
            )}
          </div>
        </div>

        {/* ═══ VERTICAL RESIZE HANDLE ═══ */}
<div
  onMouseDown={handleResizeYStart}
  style={{
    height: 8, cursor: "row-resize", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    borderRadius: 4, transition: "background .15s ease",
  }}
  onMouseEnter={e => e.currentTarget.style.background = "rgba(37,99,235,0.15)"}
  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
  title={locale === "ru" ? "Перетащите чтобы изменить высоту" : "Биіктігін өзгерту үшін сүйреңіз"}
>
  <div style={{
    height: 3, width: 40, borderRadius: 99,
    background: "rgba(15,23,42,0.15)",
  }} />
</div>

        <div style={{ ...S.card, flex: 1 }}>
  <div style={S.header}>
    <div style={{ minWidth: 0 }}>
      <div style={S.title}>Чат</div>
      <div style={S.muted}>
        {chatMessages.length > 0
          ? (locale === "ru" ? `${chatMessages.length} сообщ.` : `${chatMessages.length} хабар`)
          : (locale === "ru" ? "Обсуждение замечаний" : "Ескертулерді талқылау")}
      </div>
    </div>
  </div>

  {/* Список сообщений */}
  <div
    ref={chatScrollRef}
    onScroll={(e) => {
      const el = e.currentTarget;
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      setShowScrollBtn(distFromBottom > 100);
    }}
    style={{
      flex: 1, overflow: "auto", padding: "10px 12px",
      display: "flex", flexDirection: "column", gap: 10,
      minHeight: 0, position: "relative",
    }}
  >
    {loadingChat ? (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 20, gap: 8, color: "#94a3b8", fontSize: 12 }}>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
      style={{ animation: "erp-spin 0.9s linear infinite" }}>
      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
    </svg>
    {locale === "ru" ? "Загрузка чата…" : "Чат жүктелуде…"}
  </div>
) : chatMessages.length === 0 ? (
  <div style={S.hint}>
    {locale === "ru"
      ? "Нажмите 💬 на замечании чтобы обсудить его здесь."
      : "Ескертуді талқылау үшін 💬 басыңыз."}
  </div>
) : null}

{chatMessages.map((msg) => {
  const cm = msg.remark ? getColor(msg.remark.highlight_color, msg.remark.contact_id) : DEFAULT_COLOR;
  const hasQuote = (msg.text || "").startsWith("> ");
  const lines = hasQuote ? msg.text.split("\n") : [];
  const quoteLine = hasQuote ? lines[0].replace(/^>\s*/, "") : "";
  const mainText = hasQuote ? lines.slice(1).join("\n").trim() : (msg.text || "");
  const bubbleBg = msg.is_mine ? "#2563eb" : "#f1f5f9";
  const bubbleColor = msg.is_mine ? "#fff" : "#0f172a";

  return (
    <div
      key={msg.id}
      ref={el => { if (el) msgRefsMap.current[String(msg.id)] = el; }}
      className="erp-chat-msg-wrap"
      style={{ display: "flex", flexDirection: "column", gap: 3, alignItems: msg.is_mine ? "flex-end" : "flex-start" }}
    >
      {/* Замечание (карточка) */}
      {msg.remark && (
        <div
          onClick={() => {
            if (msg.remark?.id) {
              const er = errors.find((e) => String(e.id) === String(msg.remark.id));
              if (er) { jumpToError(er); return; }
              setScope("all");
            }
          }}
          style={{
            maxWidth: "100%", borderRadius: 12, padding: "8px 10px",
            background: cm.bg, border: `1.5px solid ${cm.border}`,
            cursor: "pointer", boxShadow: `0 2px 8px ${cm.border}22`,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: cm.pin, marginBottom: 3, display: "flex", alignItems: "center", gap: 5 }}>
            📌 {locale === "ru" ? `Стр. ${msg.remark.page}` : `Бет ${msg.remark.page}`}
            {msg.remark.author_display && (
              <span style={{ fontSize: 10, fontWeight: 600, color: "#64748b", background: "#fff", borderRadius: 999, padding: "1px 6px", border: "1px solid #e2e8f0" }}>
                {msg.remark.author_display}
              </span>
            )}
          </div>
          {msg.remark.selected_text && (
            <div style={{ fontSize: 11, color: "#64748b", fontStyle: "italic", marginBottom: 2 }}>«{msg.remark.selected_text}»</div>
          )}
          <div style={{ fontSize: 12, color: "#334155", lineHeight: 1.4 }}>{msg.remark.comment}</div>
        </div>
      )}

      {/* Пузырь сообщения */}
      {msg.text && (
        <div
          className="erp-msg-bubble"
          style={{
            maxWidth: "88%", borderRadius: 14, overflow: "hidden", position: "relative",
            background: bubbleBg,
            boxShadow: msg.is_mine ? "0 2px 8px rgba(37,99,235,0.2)" : "0 1px 4px rgba(15,23,42,0.06)",
          }}
        >
          {/* Кнопка ↩ внутри пузыря, правый верхний угол */}
          <button
            className="erp-reply-btn"
            onClick={() => setReplyingTo({ id: msg.id, text: mainText || quoteLine, author: msg.author_display })}
            title={locale === "ru" ? "Ответить" : "Жауап беру"}
            style={{
              position: "absolute", top: 4, right: 4, zIndex: 2,
              border: "none", borderRadius: 6, width: 20, height: 20,
              background: msg.is_mine ? "rgba(0,0,0,0.18)" : "rgba(15,23,42,0.08)",
              color: msg.is_mine ? "rgba(255,255,255,0.9)" : "#64748b",
              cursor: "pointer", fontSize: 11, opacity: 0,
              transition: "opacity .15s ease",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >↩</button>

          {/* Цитата */}
          {hasQuote && (
            <div
              onClick={() => {
                const authorPart = quoteLine.split(":")[0]?.trim();
                const textPart = quoteLine.split(":").slice(1).join(":").trim();
                const target = chatMessages.find((m) =>
                  m.author_display === authorPart && (m.text || "").includes(textPart.slice(0, 30))
                );
                if (target && msgRefsMap.current[String(target.id)]) {
                  const el = msgRefsMap.current[String(target.id)];
                  el.scrollIntoView({ behavior: "smooth", block: "center" });
                  el.style.transition = "background .2s ease";
                  el.style.background = "rgba(37,99,235,0.12)";
                  setTimeout(() => { el.style.background = ""; }, 1200);
                }
              }}
              style={{
                padding: "6px 28px 5px 10px",
                background: msg.is_mine ? "rgba(0,0,0,0.12)" : "rgba(37,99,235,0.07)",
                borderLeft: `3px solid ${msg.is_mine ? "rgba(255,255,255,0.5)" : "#2563eb"}`,
                fontSize: 11, color: msg.is_mine ? "rgba(255,255,255,0.75)" : "#475569",
                lineHeight: 1.4, cursor: "pointer",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}
            >{quoteLine}</div>
          )}

          {/* Текст */}
          {mainText && (
            <div style={{ padding: "7px 28px 7px 11px", color: bubbleColor, fontSize: 12.5, lineHeight: 1.4 }}>
              {mainText}
            </div>
          )}
        </div>
      )}

      {/* Время */}
      <div style={{ fontSize: 10, color: "#94a3b8", paddingLeft: 2, paddingRight: 2 }}>
        {msg.author_display} · {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
      </div>
    </div>
  );
})}
  </div>

  {/* Кнопка скролл вниз */}
  {showScrollBtn && (
    <div style={{ position: "relative", height: 0, zIndex: 10 }}>
      <button
        onClick={() => {
          if (chatScrollRef.current) {
            chatScrollRef.current.scrollTo({ top: chatScrollRef.current.scrollHeight, behavior: "smooth" });
          }
        }}
        style={{
          position: "absolute", bottom: 10, right: 12,
          width: 34, height: 34, borderRadius: "50%",
          border: "1px solid rgba(15,23,42,0.1)",
          background: "#fff", cursor: "pointer",
          boxShadow: "0 2px 12px rgba(15,23,42,0.15)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 16, color: "#2563eb",
          transition: "all .15s ease",
        }}
        title={locale === "ru" ? "Вниз" : "Төмен"}
      >↓</button>
    </div>
  )}

  {/* Ввод сообщения */}
  <div style={{ borderTop: "1px solid rgba(15,23,42,0.06)", padding: "10px 12px", flexShrink: 0 }}></div>

  {/* Ввод сообщения */}
  <div style={{ borderTop: "1px solid rgba(15,23,42,0.06)", padding: "10px 12px", flexShrink: 0 }}>

    {/* Превью ответа на сообщение */}
{replyingTo && (
  <div style={{
    marginBottom: 8, padding: "7px 10px", borderRadius: 10,
    background: "#f1f5f9", border: "1px solid #e2e8f0",
    borderLeft: "3px solid #2563eb",
    display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8,
  }}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: "#2563eb", marginBottom: 2 }}>
        ↩ {replyingTo.author}
      </div>
      <div style={{ fontSize: 11.5, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {replyingTo.text}
      </div>
    </div>
    <button
      onClick={() => setReplyingTo(null)}
      style={{ border: "none", background: "transparent", cursor: "pointer", color: "#94a3b8", fontSize: 16, lineHeight: 1, flexShrink: 0, padding: 0 }}
    >×</button>
  </div>
)}

    {/* Прикреплённое замечание (превью) */}
    {pinnedRemark && (
      <div style={{
        marginBottom: 8, padding: "7px 10px", borderRadius: 10,
        background: "#f1f5f9", border: "1px solid #e2e8f0",
        borderLeft: `3px solid ${getColor(pinnedRemark.highlight_color, pinnedRemark.contact_id).border}`,
        display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: "#64748b", marginBottom: 2 }}>
            📌 {locale === "ru" ? `Стр. ${pinnedRemark.page}` : `Бет ${pinnedRemark.page}`}
          </div>
          <div style={{ fontSize: 11.5, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {pinnedRemark.comment}
          </div>
        </div>
        <button
          onClick={() => setPinnedRemark(null)}
          style={{ border: "none", background: "transparent", cursor: "pointer", color: "#94a3b8", fontSize: 16, lineHeight: 1, flexShrink: 0, padding: 0 }}
        >
          ×
        </button>
      </div>
    )}

    <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
      <textarea
        value={chatInput}
        onChange={(e) => setChatInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendChat(); }
        }}
        placeholder={locale === "ru" ? "Написать сообщение… (Enter — отправить)" : "Хабарлама жазу… (Enter — жіберу)"}
        rows={2}
        style={{ ...S.textarea, flex: 1, margin: 0, fontSize: 12.5 }}
      />
      <button
        onClick={handleSendChat}
        disabled={sendingChat || (!chatInput.trim() && !pinnedRemark)}
        style={{
          width: 36, height: 36, border: "none", borderRadius: 10,
          background: "#2563eb", color: "#fff", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, fontSize: 16,
          opacity: (sendingChat || (!chatInput.trim() && !pinnedRemark)) ? 0.45 : 1,
          boxShadow: "0 4px 12px rgba(37,99,235,0.25)",
        }}
      >
        {sendingChat ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: "erp-spin 0.9s linear infinite" }}>
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
        ) : "→"}
      </button>
    </div>
  </div>
</div>
      </div>

{/* ═══ RESIZE HANDLE ═══ */}
<div
  onMouseDown={handleResizeStart}
  style={{
    width: 8, cursor: "col-resize", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    borderRadius: 4,
    transition: "background .15s ease",
  }}
  onMouseEnter={e => e.currentTarget.style.background = "rgba(37,99,235,0.15)"}
  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
  title={locale === "ru" ? "Перетащите чтобы изменить размер" : "Өлшемін өзгерту үшін сүйреңіз"}
>
  <div style={{
    width: 3, height: 40, borderRadius: 99,
    background: "rgba(15,23,42,0.15)",
  }} />
</div>

      {/* ═══ CENTER ═══ */}
      <div style={S.card}>
        <div style={S.header}>
          <div style={{ minWidth: 0 }}>
            <div style={S.title}>{locale === "ru" ? "Документ" : "Құжат"}</div>
            <div style={S.muted}>
              {locale === "ru"
                ? "Выделяй текст — появится заметка (Alt+мышь = скан)"
                : "Мәтінді белгіле (Alt+тышқан = скан)"}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em" }}>
              {locale === "ru" ? "Раздел" : "Бөлім"}
            </span>
            <select value={selectedPart ?? ""} onChange={handlePartChange} style={S.select}>
              {parts.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
        </div>

        <div style={S.viewerWrap}>
{loadingPdf ? (
  <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
    {/* Спиннер */}
    <div style={{ position: "relative", width: 52, height: 52 }}>
      <svg width="52" height="52" viewBox="0 0 52 52" fill="none"
        style={{ animation: "erp-spin 0.9s linear infinite", display: "block" }}>
        <circle cx="26" cy="26" r="22"
          stroke="#e2e8f0" strokeWidth="4" fill="none"/>
        <circle cx="26" cy="26" r="22"
          stroke="#2563eb" strokeWidth="4" fill="none"
          strokeLinecap="round"
          strokeDasharray="138"
          strokeDashoffset="100"/>
      </svg>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)" }}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
      </svg>
    </div>

    {/* Текст */}
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#334155", marginBottom: 4 }}>
        {locale === "ru" ? "Загружаем документ…" : "Құжат жүктелуде…"}
      </div>
      <div style={{ fontSize: 12, color: "#94a3b8" }}>
        {locale === "ru" ? "Пожалуйста, подождите" : "Күте тұрыңыз"}
      </div>
    </div>

    {/* Бегущая полоска */}
    <div style={{ width: 160, height: 3, background: "#e2e8f0", borderRadius: 99, overflow: "hidden" }}>
      <div style={{
        height: "100%", width: "40%", background: "#2563eb", borderRadius: 99,
        animation: "erp-bar 1.4s ease-in-out infinite",
      }}/>
    </div>

    <style>{`
      @keyframes erp-bar {
        0%   { transform: translateX(-100%); }
        50%  { transform: translateX(150%); }
        100% { transform: translateX(300%); }
      }
    `}</style>
  </div>
          ) : !current ? (
            <div style={{ padding: 16, color: "#94a3b8", fontSize: 13 }}>
              {locale === "ru" ? "Документ не найден." : "Құжат табылмады."}
            </div>
) : current.fileLink && !pdfUrl ? (
  <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 10 }}>
    <div style={{ fontWeight: 800, color: "#0f172a" }}>
      {locale === "ru" ? "Раздел доступен по ссылке:" : "Бөлім сілтеме арқылы:"}
    </div>

    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
      <a
        href={current.fileLink}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: "inline-block",
          padding: "9px 14px",
          borderRadius: 12,
          background: "#2563eb",
          color: "#fff",
          textDecoration: "none",
          fontWeight: 700,
          fontSize: 13,
        }}
      >
        {locale === "ru" ? "Открыть →" : "Ашу →"}
      </a>

      {current.archiveUrl && (
  <button
    onClick={() => handleDownloadArchive(current.archiveUrl, current.archiveName)}
    disabled={downloadingArchive}
    style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "9px 14px", borderRadius: 12,
      border: "1.5px solid #0891b2", color: "#0891b2",
      background: "#fff", cursor: downloadingArchive ? "default" : "pointer",
      fontWeight: 700, fontSize: 13,
      opacity: downloadingArchive ? 0.7 : 1,
      transition: "opacity .15s ease",
    }}
  >
    {downloadingArchive ? (
      <>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
          style={{ animation: "erp-spin 0.9s linear infinite", flexShrink: 0 }}>
          <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
        </svg>
        {locale === "ru" ? "Скачивание…" : "Жүктелуде…"}
      </>
    ) : (
      <>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="7 10 12 15 17 10"/>
          <line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
        {locale === "ru" ? "Скачать архив (.zip)" : "Архивті жүктеу (.zip)"}
      </>
    )}
  </button>
)}
    </div>

    {current.archiveName && (
      <div style={{ fontSize: 11.5, color: "#94a3b8" }}>
        📦 {current.archiveName}
      </div>
    )}
  </div>
) : !pdfUrl ? (
            <div style={{ padding: 16, color: "#94a3b8", fontSize: 13 }}>
              {locale === "ru" ? "PDF не найден." : "PDF табылмады."}
            </div>
          ) : (
            <PdfLoader key={pdfKey} url={pdfUrl}
              beforeLoad={
                <div style={{ height: "100%", display: "grid", placeItems: "center", color: "#94a3b8", fontSize: 13 }}>
                  {locale === "ru" ? "Открываю PDF…" : "PDF ашылуда…"}
                </div>
              }
            >
              {(pdfDocument) => (
                <PdfHighlighter
                  key={`hl-${pdfKey}`}
                  pdfDocument={pdfDocument}
                  pdfScaleValue="page-width"
                  enableAreaSelection={(event) => event.altKey}
                  onScrollChange={() => {}}
                  scrollRef={(scrollTo) => { scrollToRef.current = scrollTo; }}
                  onSelectionFinished={(position, content, hideTipAndSelection, transformSelection) => {
                    transformSelection();
                    return (
                      <SelectionPopover
                        locale={locale}
                        quote={content?.text}
                        image={content?.image || null}
                        onCancel={() => hideTipAndSelection()}
                        onSave={async (comment, colorId) => {
  const ok = await createError({
    comment,
    page: position?.pageNumber,
    quads: position,
    screenshotBase64: content?.image || null,
    selectedText: content?.text || "",
    colorId,
  });
                          if (ok) hideTipAndSelection();
                        }}
                      />
                    );
                  }}
                  highlightTransform={(highlight, _index, setTip, hideTip, _v, _s, isScrolledTo) => {
                    const er = highlight.__raw;
                    const isActive = String(er?.id) === String(activeId);
                    const isScan   = !!er?.screenshot_url;
                    const screenshotSrc = screenshotBlobMap[String(er?.id)] || null;
                    const cm = getColor(er?.highlight_color, er?.contact_id);

                    const component = isScan ? (
                      <AreaHighlight
                        isScrolledTo={isScrolledTo}
                        highlight={highlight}
                        onChange={() => {}}
                      />
                    ) : (
                      <Highlight
                        isScrolledTo={isScrolledTo}
                        position={highlight.position}
                        comment={highlight.comment}
                      />
                    );

                    const pin = (
                      <div style={{
                        position: "absolute", left: 6, top: 6,
                        width: 22, height: 22, borderRadius: 999,
                        background: isScan ? "#dc2626" : cm.pin,
                        color: "#fff", fontSize: 12, fontWeight: 900,
                        display: "grid", placeItems: "center",
                        boxShadow: isActive
                          ? `0 0 0 4px ${cm.bg}, 0 6px 16px ${cm.pin}66`
                          : `0 3px 10px ${cm.pin}55`,
                        pointerEvents: "none",
                        animation: isScan ? "scanPulse 1.5s infinite" : "none",
                      }}>!</div>
                    );

                    return (
                      <Popup
                        key={highlight.id}
                        popupContent={
                          <div style={{
                            maxWidth: 260, background: "#fff",
                            border: "1px solid rgba(15,23,42,0.09)", borderRadius: 14,
                            padding: "11px 13px",
                            boxShadow: "0 12px 36px rgba(15,23,42,0.13)",
                          }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                              <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em" }}>
                                {locale === "ru" ? "Стр." : "Бет"} {er?.page}
                              </span>
                              <span style={{
                                width: 9, height: 9, borderRadius: "50%",
                                background: cm.bg, border: `1.5px solid ${cm.border}`,
                                display: "inline-block", flexShrink: 0,
                              }} />
                            </div>
                            <div style={{ fontSize: 12.5, color: "#374151", lineHeight: 1.4 }}>{er?.comment}</div>
                            {isScan && screenshotSrc && (
                              <img src={screenshotSrc} alt="scan" style={{ marginTop: 8, width: "100%", borderRadius: 8, border: "1px solid rgba(15,23,42,0.08)", display: "block" }} />
                            )}
                            {isScan && !screenshotSrc && (
                              <div style={S.imagePlaceholder}>{locale === "ru" ? "Скан загружается..." : "Скан жүктелуде..."}</div>
                            )}
                          </div>
                        }
                        onMouseOver={(popupContent) => setTip(highlight, () => popupContent)}
                        onMouseOut={hideTip}
                      >
                        <div
                          data-hl-id={highlight.id}
                          style={{ position: "relative", overflow: "visible" }}
                        >
                          {component}
                          {pin}
                          {isActive && (
                            <div style={{
                              position: "absolute", inset: -2, borderRadius: 8,
                              outline: `2px solid ${cm.border}`,
                              outlineOffset: 1, pointerEvents: "none",
                            }} />
                          )}
                        </div>
                      </Popup>
                    );
                  }}
                  highlights={highlights}
                />
              )}
            </PdfLoader>
          )}
        </div>
      </div>

      {/* ═══ RIGHT ═══ */}
      <div style={S.card}>
        <div style={S.header}>
          <div style={{ minWidth: 0 }}>
            <div style={S.title}>{locale === "ru" ? "Замечания" : "Ескертулер"}</div>
            <div style={S.muted}>
              {locale === "ru" ? `Всего: ${errors.length}` : `Барлығы: ${errors.length}`}
            </div>
          </div>
        </div>

        {/* ── Scope tab bar ── */}
<div style={{
  display: "flex", gap: 0, flexShrink: 0,
  borderBottom: "1px solid rgba(15,23,42,0.07)",
  background: "#fff", padding: "0 12px",
}}>
  {[
    { key: "all",    labelRu: "Все замечания", labelKz: "Барлық ескертулер" },
    { key: "mine",   labelRu: "Мои",           labelKz: "Менің" },
  ].map(({ key, labelRu, labelKz }) => (
    <button
      key={key}
      onClick={() => setScope(key)}
      style={{
        flex: 1,
        padding: "10px 4px",
        border: "none",
        borderBottom: scope === key
          ? "2.5px solid #2563eb"
          : "2.5px solid transparent",
        background: "transparent",
        color: scope === key ? "#2563eb" : "#94a3b8",
        fontSize: 12,
        fontWeight: scope === key ? 800 : 600,
        cursor: "pointer",
        transition: "all .15s ease",
        whiteSpace: "nowrap",
      }}
    >
      {locale === "ru" ? labelRu : labelKz}
      {scope === key && errors.length > 0 && (
        <span style={{
          marginLeft: 5, fontSize: 10, fontWeight: 700,
          background: "#2563eb", color: "#fff",
          borderRadius: 999, padding: "1px 6px",
        }}>
          {errors.length}
        </span>
      )}
    </button>
  ))}
</div>

{menuOpenId && (
  <div
    className="erp-menu-backdrop"
    onClick={() => setMenuOpenId(null)}
  />
)}

        <div className="erp-remarks" style={S.list}>
          {errors.length === 0 && (
            <div style={S.hint}>{locale === "ru" ? "Замечаний пока нет." : "Ескертулер жоқ."}</div>
          )}

          {errors.map((er) => {
  const pos        = safeJsonParse(er.quads, null);
  const canJump    = !!(pos && pos.pageNumber && scrollToRef.current);
  const screenshotSrc = screenshotBlobMap[String(er.id)] || null;
  const isActive   = String(er.id) === String(activeId);
  const isEditing  = String(er.id) === String(editingId);
  const isMenuOpen = String(er.id) === String(menuOpenId);
const cm = getColor(er?.highlight_color, er?.contact_id);
  const isDeleting = String(er.id) === String(deletingId);
  const isSaving   = String(er.id) === String(savingId);
  const canEdit   = er.can_edit === true;
const canDelete = er.can_delete === true;
const hasMenu   = canEdit || canDelete;

  return (
    <div
      key={er.id}
      style={{
        border: isActive ? `1.5px solid ${cm.border}` : "1px solid rgba(15,23,42,0.08)",
        background: isActive ? cm.bg : "#fff",
        borderRadius: 14, padding: 12,
        boxShadow: isActive
          ? `0 0 0 3px ${cm.border}22, 0 4px 12px rgba(15,23,42,0.06)`
          : "0 1px 4px rgba(15,23,42,0.05)",
        transition: "all .18s ease",
        position: "relative",
        opacity: isDeleting ? 0.5 : 1,
      }}
    >
      {/* Шапка карточки */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 5 }}>
        <div
          style={{ fontWeight: 800, fontSize: 12.5, color: cm.pin, display: "flex", alignItems: "center", gap: 5, cursor: canJump ? "pointer" : "default", flex: 1 }}
          onClick={() => { if (canJump && !isEditing) jumpToError(er); }}
        >
          <span style={{ width: 9, height: 9, borderRadius: "50%", background: cm.bg, border: `1.5px solid ${cm.border}`, display: "inline-block", flexShrink: 0 }} />
          {locale === "ru" ? `Стр. ${er.page}` : `Бет ${er.page}`}

          {er.author_display && (
  <span style={{
    fontSize: 11, fontWeight: 700,
    color: er.is_mine ? "#2563eb" : "#475569",
    background: er.is_mine ? "rgba(37,99,235,0.12)" : "#f1f5f9",
    border: er.is_mine ? "1px solid rgba(37,99,235,0.25)" : "1px solid #cbd5e1",
    borderRadius: 999, padding: "2px 8px", marginLeft: 6,
    letterSpacing: "0.01em", flexShrink: 0,
  }}>
    {er.author_display}
  </span>
)}
        </div>

        <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
          {er.screenshot_url && (
            <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: "#dc2626", borderRadius: 999, padding: "2px 7px" }}>SCAN</span>
          )}
          {canJump && !isEditing && (
            <span
              onClick={() => jumpToError(er)}
              style={{ fontSize: 11, fontWeight: 600, color: "#2563eb", background: "rgba(37,99,235,0.08)", borderRadius: 999, padding: "2px 7px", cursor: "pointer" }}
            >
              → {locale === "ru" ? "перейти" : "өту"}
            </span>
          )}

          <button
  onClick={() => handlePinToChat(er)}
  title={locale === "ru" ? "Обсудить в чате" : "Чатта талқылау"}
  style={{
    width: 26, height: 26, borderRadius: 8,
    border: "1px solid rgba(15,23,42,0.1)",
    background: "#fff", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 13, flexShrink: 0,
  }}
>
  💬
</button>

          {/* ⋯ меню — только если есть права */}
{hasMenu && (
  <div style={{ position: "relative" }}>
    <button
      onClick={() => setMenuOpenId(isMenuOpen ? null : String(er.id))}
      style={{
        width: 26, height: 26, borderRadius: 8, border: "1px solid rgba(15,23,42,0.1)",
        background: isMenuOpen ? "#f1f5f9" : "#fff", cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 14, color: "#64748b", flexShrink: 0,
      }}
      title={locale === "ru" ? "Действия" : "Әрекеттер"}
    >
      ⋯
    </button>

    {isMenuOpen && (
      <div style={{
        position: "absolute", right: 0, top: 30, zIndex: 999,
        background: "#fff", border: "1px solid rgba(15,23,42,0.1)",
        borderRadius: 12, boxShadow: "0 8px 28px rgba(15,23,42,0.14)",
        overflow: "hidden", minWidth: 150,
      }}>
        {canEdit && (
          <button
            onClick={() => handleStartEdit(er)}
            style={{
              width: "100%", textAlign: "left", padding: "10px 14px",
              border: "none", background: "transparent", cursor: "pointer",
              fontSize: 12.5, fontWeight: 600, color: "#0f172a",
              display: "flex", alignItems: "center", gap: 8,
              borderBottom: canDelete ? "1px solid rgba(15,23,42,0.06)" : "none",
            }}
          >
            ✏️ {locale === "ru" ? "Редактировать" : "Өңдеу"}
          </button>
        )}
        {canDelete && (
          <button
            onClick={() => handleDeleteError(er.id)}
            disabled={isDeleting}
            style={{
              width: "100%", textAlign: "left", padding: "10px 14px",
              border: "none", background: "transparent", cursor: "pointer",
              fontSize: 12.5, fontWeight: 600, color: "#dc2626",
              display: "flex", alignItems: "center", gap: 8,
            }}
          >
            🗑 {locale === "ru" ? "Удалить" : "Жою"}
          </button>
        )}
      </div>
    )}
  </div>
)}
        </div>
      </div>

      {/* Скриншот */}
      {er.screenshot_url && (
        screenshotSrc
          ? <img src={screenshotSrc} alt="scan" style={{ marginTop: 6, width: "100%", borderRadius: 9, border: "1px solid rgba(15,23,42,0.08)", display: "block" }} />
          : <div style={S.imagePlaceholder}>{locale === "ru" ? "Скан загружается..." : "Скан жүктелуде..."}</div>
      )}

      {/* Режим редактирования */}
      {isEditing ? (
  <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
    <textarea
            value={editDraft.comment}
            onChange={(e) => setEditDraft((s) => ({ ...s, comment: e.target.value }))}
            rows={3}
            style={S.textarea}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={handleCancelEdit}
              style={{ flex: 1, border: "1px solid rgba(15,23,42,0.1)", background: "#f8fafc", borderRadius: 10, padding: "8px 0", cursor: "pointer", fontWeight: 700, fontSize: 12, color: "#334155" }}
            >
              {locale === "ru" ? "Отмена" : "Болдырмау"}
            </button>
            <button
              onClick={() => handleSaveEdit(er.id)}
              disabled={isSaving}
              style={{ flex: 1, border: "none", background: "#2563eb", color: "#fff", borderRadius: 10, padding: "8px 0", cursor: isSaving ? "default" : "pointer", fontWeight: 700, fontSize: 12, opacity: isSaving ? 0.7 : 1 }}
            >
              {isSaving ? "..." : (locale === "ru" ? "Сохранить" : "Сақтау")}
            </button>
          </div>
        </div>
      ) : (
  <div style={{ marginTop: 5 }}>
    {er.selected_text && (
      <div style={{
        fontSize: 11.5, color: "#64748b", background: "#f8fafc",
        border: "1px solid #e2e8f0", borderLeft: `3px solid ${cm.border}`,
        borderRadius: "0 8px 8px 0", padding: "5px 9px",
        marginBottom: 6, fontStyle: "italic", lineHeight: 1.4,
        overflow: "hidden", textOverflow: "ellipsis",
        display: "-webkit-box", WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
      }}>
        «{er.selected_text}»
      </div>
    )}
    <div style={{ fontSize: 12.5, color: "#334155", lineHeight: 1.4 }}>
      {er.comment}
    </div>
  </div>
)}
    </div>
  );
})}

          {/* Add manually */}
                    {!isReadOnly && (
            <div style={{ marginTop: 6, borderTop: "1px solid rgba(15,23,42,0.06)", paddingTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 9 }}>
              {locale === "ru" ? "Добавить вручную" : "Қолмен қосу"}
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              <input
                type="number"
                placeholder={locale === "ru" ? "Номер страницы" : "Бет нөмірі"}
                value={newError.page}
                onChange={(e) => setNewError((s) => ({ ...s, page: e.target.value }))}
                style={S.input}
              />
              <textarea
                placeholder={locale === "ru" ? "Описание замечания" : "Ескерту сипаттамасы"}
                value={newError.comment}
                onChange={(e) => setNewError((s) => ({ ...s, comment: e.target.value }))}
                rows={3}
                style={S.textarea}
              />
              <button onClick={handleAddErrorManual} style={S.btnWarn} disabled={creatingError}>
                {creatingError
                  ? (locale === "ru" ? "Сохранение..." : "Сақталуда...")
                  : (locale === "ru" ? "➕ Добавить замечание" : "➕ Ескертуді қосу")}
              </button>
            </div>
          </div>
          )}
        </div>
        
      </div>
    </div>
  );
}